import { Component, EventEmitter, Inject, Injector, Input, OnDestroy, OnInit, Output, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';

import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionService, AuthService, cleanObject, CustomFormatter, ISitemap, ISitemapCommand, SITEMAP } from 'pbc.angular';

import { IPostOrdneFeldOptionenRequest, IPostOrdneFeldOptionenResponse } from 'fa-kt.types';
import { PostOrdneFeldOptionenCommandService } from '../service';

@Component({
  selector: 'fa-kt-post-ordne-feld-optionen',
  templateUrl: './post-ordne-feld-optionen.component.html',
  styleUrls: ['./post-ordne-feld-optionen.component.css'],
})
export class PostOrdneFeldOptionenCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IPostOrdneFeldOptionenRequest) {
    this.patch(value);
  }
  @Output() valueChanged: EventEmitter<IPostOrdneFeldOptionenRequest> = new EventEmitter<IPostOrdneFeldOptionenRequest>();
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostOrdneFeldOptionenResponse> = new EventEmitter<IPostOrdneFeldOptionenResponse>();

  /* << fields    */
  /*    fields >> */

  get modalRef() {
    return this.injector.get(NzModalRef);
  }

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private injector: Injector,
    private viewContainerRef: ViewContainerRef,
    private modal: NzModalService,
    private message: NzMessageService,
    private actions: ActionService,
    public auth: AuthService,
    public postOrdneFeldOptionen: PostOrdneFeldOptionenCommandService /* << more    */,
  ) /*    more >> */
  {
    this.command = sitemap.FELDER.Commands.POST_ORDNE_FELD_OPTIONEN;
    this.form = this.fb.group({
      feldOptionen: this.fb.group({
        id: [null, []],
        feld: [null, [Validators.required]],
        option: [null, [Validators.required]],
        akkusativ: [null, []],
        nominativ: [null, []],
        dativ: [null, []],
        genitiv: [null, []],
        standard: [null, []],
      }),
    });
    /* << constructor    */
    /*    constructor >> */
    this.patch();
  }

  ngOnInit() {
    this.postOrdneFeldOptionen.prepare().catch((e) => console.error(e));
    this.subscriptions.push(
      this.finished.subscribe((result) => {
        if (this.modalRef) {
          this.modalRef.destroy(result);
        }
      }),
      this.valid.subscribe((valid) => {
        if (valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.form.valueChanges.subscribe((value: IPostOrdneFeldOptionenRequest) => {
        this.valueChanged.emit(value);
        this.valid.emit(this.form.valid);
      }),
      this.feldOptionenId?.valueChanges.subscribe(async (id) => {
        /* << on-feld-optionen-id-change    */
        /*    on-feld-optionen-id-change >> */
      }) as Subscription,
      this.feldOptionenFeld?.valueChanges.subscribe(async (feld) => {
        /* << on-feld-optionen-feld-change    */
        /*    on-feld-optionen-feld-change >> */
      }) as Subscription,
      this.feldOptionenOption?.valueChanges.subscribe(async (option) => {
        /* << on-feld-optionen-option-change    */
        /*    on-feld-optionen-option-change >> */
      }) as Subscription,
      this.feldOptionenAkkusativ?.valueChanges.subscribe(async (akkusativ) => {
        /* << on-feld-optionen-akkusativ-change    */
        /*    on-feld-optionen-akkusativ-change >> */
      }) as Subscription,
      this.feldOptionenNominativ?.valueChanges.subscribe(async (nominativ) => {
        /* << on-feld-optionen-nominativ-change    */
        /*    on-feld-optionen-nominativ-change >> */
      }) as Subscription,
      this.feldOptionenDativ?.valueChanges.subscribe(async (dativ) => {
        /* << on-feld-optionen-dativ-change    */
        /*    on-feld-optionen-dativ-change >> */
      }) as Subscription,
      this.feldOptionenGenitiv?.valueChanges.subscribe(async (genitiv) => {
        /* << on-feld-optionen-genitiv-change    */
        /*    on-feld-optionen-genitiv-change >> */
      }) as Subscription,
      this.feldOptionenStandard?.valueChanges.subscribe(async (standard) => {
        /* << on-feld-optionen-standard-change    */
        /*    on-feld-optionen-standard-change >> */
      }) as Subscription,
      /* << subscibe    */
      /*    subscibe >> */
    );
    /* << init    */
    /*    init >> */
  }

  ngOnDestroy() {
    /* << end    */
    /*    end >> */
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IPostOrdneFeldOptionenRequest) {
    if (value) {
      value = cleanObject(value);
      if (value.feldOptionen) {
        value.feldOptionen = cleanObject(value.feldOptionen);
      }
      this.form.patchValue(value);
    }
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload.feldOptionen) {
        payload.feldOptionen = cleanObject(payload.feldOptionen);
      }
      const response: IPostOrdneFeldOptionenResponse = await this.postOrdneFeldOptionen.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls[validation.property].get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  get feldOptionenId() {
    return this.form.controls.feldOptionen.get('id');
  }
  get feldOptionenFeld() {
    return this.form.controls.feldOptionen.get('feld');
  }
  get feldOptionenOption() {
    return this.form.controls.feldOptionen.get('option');
  }
  get feldOptionenAkkusativ() {
    return this.form.controls.feldOptionen.get('akkusativ');
  }
  get feldOptionenNominativ() {
    return this.form.controls.feldOptionen.get('nominativ');
  }
  get feldOptionenDativ() {
    return this.form.controls.feldOptionen.get('dativ');
  }
  get feldOptionenGenitiv() {
    return this.form.controls.feldOptionen.get('genitiv');
  }
  get feldOptionenStandard() {
    return this.form.controls.feldOptionen.get('standard');
  }
}
